import {
    SearchFilter,
    SortModel
} from "@/lib/depict-client/types/api/ProductListingRequestV3"
import logger from "@/lib/logger/logger-init"
import { GeneralSize } from "@/maps/centra-size-to-generic-size"
import generalSizeToSizeMap from "@/maps/generic-size-to-centra-size"
import { FilterType } from "./filterReducer"
import { filterConfig, sortConfig } from "./types/options"

// Function to map filtered searchParams to filters for API
export const mapSearchParamsToFilters = (
    searchParams: { [key: string]: string },
    shouldTransformSize?: boolean
): { filters: SearchFilter[]; sort: unknown } => {
    const filters: SearchFilter[] = []

    // Default sort configuration
    let sort: unknown = undefined

    Object.keys(searchParams).forEach(param => {
        if (param === "sort") {
            const sortValue = searchParams[param]?.split("&")
            if (sortValue && sortValue.length === 2) {
                const [field, order] = sortValue // Destructure field and order from the query
                const matchedSortConfig = Object.values(sortConfig).find(
                    config => config.field === field && config.order === order
                )

                if (matchedSortConfig) {
                    // Use the matched sort configuration from `sortConfig`
                    sort = matchedSortConfig
                } else {
                    // Fallback if no match is found
                    logger.error(
                        `No matching sort configuration found for: ${field}, ${order}`
                    )
                }
            }
        } else {
            // Handle as a filter
            const config = filterConfig.find(config => config.field === param)
            if (config) {
                let values = searchParams[param].split("&")

                if (param === "size" && shouldTransformSize) {
                    values = values.flatMap(
                        generalSize =>
                            generalSizeToSizeMap.get(generalSize as GeneralSize) || [
                                generalSize
                            ]
                    )
                    values = Array.from(new Set(values)) // Remove duplicates
                }

                filters.push({
                    data: values,
                    field: config.field ?? "",
                    op: config.op!
                })
            }
        }
    })

    return { filters, sort: sort !== undefined ? sort : undefined }
}

export const updateQueryParamsWithSelectedOptions = (
    router: any,
    pathname: string,
    filterState: FilterType[],
    sort: SortModel | undefined // Add the sort parameter
) => {
    const searchParams = new URLSearchParams(router.query)

    // Handle filter options
    filterState.forEach(option => {
        const { field, data } = option
        if (data && data.length > 0) {
            searchParams.set(field, data.join("&"))
        } else {
            searchParams.delete(field)
        }
    })

    // Handle sort option
    if (sort) {
        searchParams.set("sort", `${sort.field}&${sort.order}`)
    } else {
        searchParams.delete("sort") // Remove sort if none is specified
    }

    // Push updated params to the router
    router.push(`${pathname}?${searchParams.toString()}`, { shallow: true })
}

export const transformedFilterOptions = (filterState: FilterType[]) => {
    return filterState.map(option => ({
        field: option.field,
        op: option.op as SearchFilter["op"],
        data: Array.isArray(option.data)
            ? option.field === filterConfig[0].field
                ? option.data.flatMap(
                      dataItem => generalSizeToSizeMap.get(dataItem as GeneralSize) || []
                  )
                : option.data
            : [option.data],
        id: option.field // Use `field` as `id` for grouping
    }))
}
